import { FC, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ExperienceAnswerStep } from 'shared/models';
import { ListWrapper } from '../answer-list-wrapper';
import { AnswerStepContent } from '../answer-step-content';
import { useSetScopedAtom } from '@lemon8/web-app-shared/ssr';
import { $CurrentExperienceAnswerFold } from '../../atoms';
import { useViewTracking } from '~/utils/use-view-tracking';
import { ModuleType } from '~/utils/module-type';
import { ClickPosition } from '~/utils/click-position';
import { useIOSVersion } from '../../utils/use-ios-version';
import cls from 'classnames';

import './style.scss';

interface PartialAnswerBodyProps {
  stepContent: ExperienceAnswerStep[];
  isNumber: boolean;
}

export const PartialAnswerBody: FC<PartialAnswerBodyProps> = (props) => {
  const { stepContent, isNumber } = props;
  const { t } = useTranslation();
  const setIsFold = useSetScopedAtom($CurrentExperienceAnswerFold);

  const iOSVersion = useIOSVersion();

  const isLowerThanIOS17 = useMemo(() => {
    if (iOSVersion) {
      return iOSVersion.major < 17;
    }
    return false;
  }, [iOSVersion]);

  const partialAnswerRef = useViewTracking({
    onView: useCallback(() => {
      window.Tea('wap_cta_show', {
        current_page_module: ModuleType.experience_content,
        current_position: ClickPosition.experience_answer,
        is_cta: 0,
      });
      window.Tea('wap_cta_show', {
        current_page_module: ModuleType.experience_content,
        current_position: ClickPosition.experience_seemore,
        is_cta: 0,
      });
    }, []),
  });

  const stepContent1 = useMemo(() => {
    return {
      title: stepContent[0].title,
      content: stepContent[0].content,
      sourceList: stepContent[0].sourceList,
    };
  }, [stepContent[0]]);

  const stepContent2 = useMemo(() => {
    return {
      title: stepContent[1].title,
      content: stepContent[1].content,
      sourceList: stepContent[1].sourceList,
    };
  }, [stepContent[1]]);

  const handleClick = useCallback(() => {
    window.Tea('wap_click', {
      current_page_module: ModuleType.experience_content,
      current_position: ClickPosition.experience_seemore,
      is_cta: 0,
    });
    setIsFold(false);
  }, []);

  const renderFoldedListStep = () => {
    return (
      <li className="answer-step-content-folded-li">
        <div className="answer-step-content-folded" ref={partialAnswerRef}>
          <p className="answer-step-content-folded-inner">
            <object className={cls('read-more', { 'low-version-ios': isLowerThanIOS17 })}>
              {'... '}
              <span className="read-more-btn" onClick={handleClick}>
                {t('read_more')}
              </span>
            </object>
            <strong>{stepContent2.title}: </strong>
            <span>{stepContent2.content}</span>
          </p>
        </div>
      </li>
    );
  };

  return (
    <div className="partial-answer-body">
      <ListWrapper isNumber={isNumber}>
        <AnswerStepContent
          title={stepContent1.title}
          content={stepContent1.content}
          sourceList={stepContent1.sourceList}
        />
        {renderFoldedListStep()}
      </ListWrapper>
    </div>
  );
};
